import { useEffect, useRef, useState } from 'react';
import { X, Filter, Bookmark, Check } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { AREE, AREE_ORDER } from '../../constants/areas';
import { addView } from '../../lib/savedViews';

/**
 * Filtri trasversali per le viste drill-down (Periodo, Oggi).
 *
 * Filtri supportati:
 *  - area: subset di AREE_ORDER (business/lavoro/personale/salute)
 *  - stato: subset di ['on_track', 'a_rischio', 'raggiunto', 'attivo']
 *  - confidence: subset di ['alta', 'media', 'bassa']  (alta >=0.7, media 0.4-0.7, bassa <0.4)
 *
 * Props:
 *  - filters: { area: [], stato: [], confidence: [] }
 *  - onChange: (newFilters) => void
 *  - hide: array di nomi ('confidence', etc.) da nascondere se non applicabili
 */
const STATO_OPTIONS = [
  { v: 'on_track', label: 'On-track', color: 'var(--qp-ink)' },
  { v: 'a_rischio', label: 'A rischio', color: '#E5484D' },
  { v: 'raggiunto', label: 'Raggiunti', color: 'var(--qp-success)' },
];
const CONF_OPTIONS = [
  { v: 'alta', label: 'Alta · ≥0.7', color: 'var(--qp-success)' },
  { v: 'media', label: 'Media · 0.4-0.7', color: '#F59E0B' },
  { v: 'bassa', label: 'Bassa · <0.4', color: '#E5484D' },
];

export default function FilterBar({ filters, onChange, hide = [] }) {
  const loc = useLocation();
  const f = { area: [], stato: [], confidence: [], ...filters };
  const total = f.area.length + f.stato.length + f.confidence.length;

  const toggle = (key, val) => {
    const cur = f[key] || [];
    const next = cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val];
    onChange({ ...f, [key]: next });
  };
  const clear = () => onChange({ area: [], stato: [], confidence: [] });

  // Popover inline al posto di window.prompt (coerenza col design system)
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const saveInputRef = useRef(null);

  const openSave = () => {
    setSaveName(suggestName({ route: loc.pathname, filters: f }));
    setSaveOpen(true);
  };
  useEffect(() => {
    if (saveOpen && saveInputRef.current) saveInputRef.current.focus();
  }, [saveOpen]);
  const confirmSave = () => {
    const name = (saveName || '').trim();
    if (!name) return;
    addView({ name, route: loc.pathname, search: loc.search });
    toast.success(`Vista "${name}" salvata`);
    setSaveOpen(false);
  };

  return (
    <div className="border border-line bg-tint/50 px-4 py-3 mb-6" data-testid="filter-bar">
      <div className="flex items-start gap-4 flex-wrap">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-soft flex items-center gap-1.5 pt-1">
          <Filter size={11} strokeWidth={1.5} /> Filtri
        </span>

        {/* Area chips */}
        {!hide.includes('area') && (
          <Group label="Area">
            {AREE_ORDER.map((a) => {
              const meta = AREE[a];
              const on = f.area.includes(a);
              return (
                <Chip
                  key={a}
                  label={meta.label}
                  active={on}
                  color={meta.color}
                  onClick={() => toggle('area', a)}
                  testid={`filter-area-${a}`}
                />
              );
            })}
          </Group>
        )}

        {!hide.includes('stato') && (
          <Group label="Stato">
            {STATO_OPTIONS.map((s) => (
              <Chip
                key={s.v}
                label={s.label}
                active={f.stato.includes(s.v)}
                color={s.color}
                onClick={() => toggle('stato', s.v)}
                testid={`filter-stato-${s.v}`}
              />
            ))}
          </Group>
        )}

        {!hide.includes('confidence') && (
          <Group label="Confidence">
            {CONF_OPTIONS.map((c) => (
              <Chip
                key={c.v}
                label={c.label}
                active={f.confidence.includes(c.v)}
                color={c.color}
                onClick={() => toggle('confidence', c.v)}
                testid={`filter-conf-${c.v}`}
              />
            ))}
          </Group>
        )}

        {total > 0 && (
          <div className="ml-auto flex items-center gap-3 pt-1 relative">
            <button
              onClick={openSave}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-soft hover:text-ink"
              title="Salva la combinazione corrente di filtri come vista"
              data-testid="filter-save-view"
            >
              <Bookmark size={11} strokeWidth={1.5} /> Salva vista
            </button>
            {saveOpen && (
              <div className="absolute right-0 top-8 z-30 bg-paper border border-line shadow-lg p-3 w-72" data-testid="filter-save-popover">
                <label className="mono-label block mb-1" htmlFor="filter-save-name">Nome della vista</label>
                <input
                  id="filter-save-name"
                  ref={saveInputRef}
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') confirmSave();
                    if (e.key === 'Escape') setSaveOpen(false);
                  }}
                  className="w-full border border-line bg-canvas px-2 py-1.5 font-body text-sm focus:outline-none focus:border-ink mb-2"
                  data-testid="filter-save-name-input"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setSaveOpen(false)}
                    className="px-2 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-soft hover:text-ink"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={confirmSave}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-ink text-canvas font-mono text-[10px] uppercase tracking-[0.15em] hover:bg-accent"
                    data-testid="filter-save-confirm"
                  >
                    <Check size={11} strokeWidth={1.5} /> Salva
                  </button>
                </div>
              </div>
            )}
            <button
              onClick={clear}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-soft hover:text-accent"
              data-testid="filter-clear"
            >
              <X size={11} strokeWidth={1.5} /> Pulisci · {total}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Group({ label, children }) {
  return (
    <div className="flex items-baseline gap-2 flex-wrap">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mute">{label}</span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Chip({ label, active, color, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-1 border transition-colors"
      style={
        active
          ? { borderColor: color, backgroundColor: color, color: 'var(--qp-canvas)' }
          : { borderColor: 'var(--qp-border)', color: 'var(--qp-text-soft)' }
      }
      data-testid={testid}
      data-active={active ? '1' : '0'}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

/**
 * Suggerisce un nome leggibile per una vista basato su route + filtri.
 */
function suggestName({ route, filters }) {
  const parts = [];
  const f = filters || {};
  if (f.stato?.length) parts.push(f.stato.map(s => s.replace('_', ' ')).join('+'));
  if (f.area?.length) parts.push(f.area.join('+'));
  if (f.confidence?.length) parts.push('conf:' + f.confidence.join('+'));
  const where = ROUTE_LABEL[route] || (route === '/' ? 'Dashboard' : route.replace(/^\//, ''));
  return parts.length ? `${parts.join(' · ')} — ${where}` : where;
}

const ROUTE_LABEL = {
  '/': 'Dashboard',
  '/oggi': 'Oggi',
  '/timeline': 'Timeline',
  '/calendario': 'Calendario',
};

/**
 * Helper: applica i filtri a una collezione di KR/azioni/MIT/target.
 * Restituisce true se l'item passa il filtro.
 *
 * Item può avere:
 *  - area (string)
 *  - stato/on_track (string)
 *  - confidence (number 0..1)
 *  - raggiunto (bool)
 */
export function passesFilter(item, filters) {
  const f = { area: [], stato: [], confidence: [], ...filters };

  if (f.area.length > 0) {
    const a = (item.area || '').toLowerCase();
    if (!f.area.includes(a)) return false;
  }

  if (f.stato.length > 0) {
    const stati = new Set(f.stato);
    let match = false;
    if (item.raggiunto === true && stati.has('raggiunto')) match = true;
    if (item.stato === 'raggiunto' && stati.has('raggiunto')) match = true;
    if (item.on_track && stati.has(item.on_track)) match = true;
    if (!match) return false;
  }

  if (f.confidence.length > 0) {
    const c = item.confidence;
    if (c === null || c === undefined) return false;
    const n = Number(c);
    const bucket = n >= 0.7 ? 'alta' : n >= 0.4 ? 'media' : 'bassa';
    if (!f.confidence.includes(bucket)) return false;
  }

  return true;
}
